/**
 * Home — the customer landing screen. One aggregate call (`/api/home`) drives
 * everything: a personal hero (greeting + the month's story in one sentence),
 * the runs that need the user RIGHT NOW (a question card and a live card,
 * mirroring the Active page's CTAs), the animated workshop vignette, the
 * month-to-date stat band with a 14-day histogram, quick-launch flow cards,
 * and the latest deliverables.
 *
 * Polling: 10s while anything is awaiting/running (so the question card and
 * captions track reality), 60s when the workshop is quiet.
 */
import { Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { HomeAttentionRun, HomeOverview, RunSummary } from '@portal/contracts'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listFlows } from '../api/flows'
import { getHomeOverview } from '../api/home'
import { useSession } from '../auth/useSession'
import { HomeDiorama } from '../components/home/HomeDiorama'
import { HomeStats } from '../components/home/HomeStats'
import { firstNameFor, fmtToday } from '../components/home/home-format'
import { LatestResults } from '../components/home/LatestResults'
import { QuickFlows } from '../components/home/QuickFlows'
import { homeKey, keys } from '../hooks/queryClient'
import { describeApiError, useI18n } from '../i18n'

const ACTIVE_POLL_MS = 10_000
const IDLE_POLL_MS = 60_000

export function HomePage() {
  const { user, contractor } = useSession()
  const { t, locale } = useI18n()
  const home = useQuery({
    queryKey: homeKey,
    queryFn: getHomeOverview,
    // Always refetch on mount: coming back from a just-finished run must show
    // the new numbers, not a 30s-stale snapshot.
    staleTime: 0,
    refetchInterval: (query) => {
      const d = query.state.data
      return d && (d.attention.length > 0 || d.live.length > 0) ? ACTIVE_POLL_MS : IDLE_POLL_MS
    }
  })
  const flows = useQuery({ queryKey: keys.flows(), queryFn: listFlows })
  // Prefer the flow catalog's operator-set display name over the humanized id.
  const flowNames = new Map((flows.data ?? []).map((f) => [f.flowId, f.displayName]))

  const now = new Date()
  const hour = now.getHours()
  const greeting = t.home.greeting(hour)
  const firstName = firstNameFor(user?.displayName, user?.email, t.home.greetingFallbackName)
  const todayLabel = fmtToday(now, locale)
  const tenantName = contractor?.name ?? t.home.yourTeam

  return (
    <div className="page portal-page home-page">
      <section className="home-hero">
        <div className="home-hero-main">
          <div className="home-greeting-block">
            <div className="home-kicker">
              {todayLabel} · {tenantName}
            </div>
            <h1 className="home-greeting">
              {greeting}, {firstName}
            </h1>
            <HomeLede overview={home.data} />
          </div>

          {home.isLoading && (
            <div className="portal-loading">
              <Spinner size={18} /> {t.home.loadingWorkshop}
            </div>
          )}
          {home.isError && <Notice kind="danger">{describeApiError(t, home.error)}</Notice>}
          {home.data && <HeroCards overview={home.data} flowNames={flowNames} />}
        </div>

        <StageCard overview={home.data} />
      </section>

      {home.data && <HomeStats stats={home.data.stats} tenantName={tenantName} />}

      {(flows.data || flows.isLoading) && <QuickFlows flows={flows.data ?? []} />}

      {home.data && <LatestResults recent={home.data.recent} flowNames={flowNames} />}
    </div>
  )
}

/** The month's story in one warm sentence, honest at every zero-state. */
function HomeLede({ overview }: { overview: HomeOverview | undefined }) {
  const { t } = useI18n()
  const m = t.home
  if (!overview) return <p className="home-lede">{m.ledeDefault}</p>
  const { stats, attention, live } = overview
  if (stats.runsCompleted === 0 && live.length === 0 && attention.length === 0) {
    return <p className="home-lede">{m.ledeEmpty}</p>
  }
  if (stats.runsCompleted === 0) {
    return <p className="home-lede">{m.ledeFirstRuns(attention.length, live.length)}</p>
  }
  return (
    <p className="home-lede">
      {m.ledeDeliveredPrefix} <strong>{m.docsCount(stats.docsDelivered)}</strong> {m.ledeDeliveredMid}{' '}
      <strong>{m.runsCount(stats.runsCompleted)}</strong> {m.ledeDeliveredSuffix}
      {m.ledeTail(attention.length, live.length)}
    </p>
  )
}

/** The "needs you" (awaiting) and "in motion" (running) cards. */
function HeroCards({ overview, flowNames }: { overview: HomeOverview; flowNames: Map<string, string> }) {
  const { t } = useI18n()
  const m = t.home
  const { attention, live } = overview
  // Absent means "we could not read it", which is not the same as none — so it
  // renders nothing and, deliberately, does not hold back "all quiet" either.
  const waiting = overview.memoriesWaiting?.total ?? 0
  if (attention.length === 0 && live.length === 0 && waiting === 0) {
    return (
      <div className="home-quiet card">
        <div className="home-quiet-icon">
          <Icon name="spark" size={18} />
        </div>
        <div className="home-quiet-text">
          <div className="home-quiet-title">{m.allQuietTitle}</div>
          <div className="muted">{m.allQuietSub}</div>
        </div>
        <Link className="btn btn-sm" to="/flows">
          {m.browseFlows} <Icon name="chevron" size={13} />
        </Link>
      </div>
    )
  }
  return (
    <>
      {attention[0] && (
        <AttentionCard
          run={attention[0]}
          extra={attention.length - 1}
          displayName={flowNames.get(attention[0].flowId) ?? attention[0].displayName}
        />
      )}
      {overview.memoriesWaiting && overview.memoriesWaiting.total > 0 && (
        <MemoriesWaitingCard waiting={overview.memoriesWaiting} />
      )}
      {live[0] && (
        <LiveCard
          run={live[0]}
          extra={live.length - 1}
          displayName={flowNames.get(live[0].flowId) ?? live[0].displayName}
        />
      )}
    </>
  )
}

/**
 * Memories of this person's own that are waiting on them.
 *
 * The same shape as the "needs you" card and deliberately NOT the same colour:
 * a run that stopped to ask a question is blocked, and this is not. Someone who
 * sees both should be able to tell in one glance which one is holding work up.
 */
function MemoriesWaitingCard({ waiting }: { waiting: NonNullable<HomeOverview['memoriesWaiting']> }) {
  const { t } = useI18n()
  const m = t.memories
  const to = waiting.flowId ? `/memories?flowId=${encodeURIComponent(waiting.flowId)}` : '/memories'
  return (
    <Link className="home-memory" to={to}>
      <div className="home-memory-icon" aria-hidden>
        <Icon name="spark" size={18} />
      </div>
      <div className="home-memory-text">
        <div className="home-memory-title">{m.homeTitle(waiting.total)}</div>
        <div className="home-memory-sub muted">
          {waiting.displayName ? m.homeSubWithSmith(waiting.displayName) : m.homeSub}
        </div>
      </div>
      <span className="btn btn-sm home-card-cta">
        {m.homeCta} <Icon name="chevron" size={13} />
      </span>
    </Link>
  )
}

function AttentionCard({ run, extra, displayName }: { run: HomeAttentionRun; extra: number; displayName: string }) {
  const { t, formatAgo } = useI18n()
  const m = t.home
  const navigate = useNavigate()
  const open = () => navigate(`/runs/${encodeURIComponent(run.executionId)}`)
  const asked = formatAgo(run.askedAt ?? run.startedAt)
  return (
    <div className="home-attn" onClick={open}>
      <div className="home-attn-icon" aria-hidden>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 4h16v12H7l-3 3z" />
          <path d="M8 9h8M8 13h5" />
        </svg>
      </div>
      <div className="home-attn-text">
        <div className="home-attn-title">{m.hasQuestionForYou(displayName)}</div>
        <div className="home-attn-sub muted">
          {run.question ? m.attnWithQuestion(run.question, asked) : m.attnWaitingReply(asked)}
          {extra > 0 && (
            <>
              {' · '}
              <Link to="/active" onClick={(e) => e.stopPropagation()}>
                {m.moreWaiting(extra)}
              </Link>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary btn-sm home-card-cta"
        onClick={(e) => {
          e.stopPropagation()
          open()
        }}
      >
        {m.answer} <Icon name="chevron" size={13} />
      </button>
    </div>
  )
}

function LiveCard({ run, extra, displayName }: { run: RunSummary; extra: number; displayName: string }) {
  const { t, formatAgo } = useI18n()
  const m = t.home
  const navigate = useNavigate()
  const open = () => navigate(`/runs/${encodeURIComponent(run.executionId)}`)
  return (
    <div className="home-live card" onClick={open}>
      <span className="live-pulse">{m.liveBadge}</span>
      <div className="home-live-text">
        <div className="home-live-title">
          {displayName}
          {run.briefPreview ? ` · ${run.briefPreview}` : ''}
        </div>
        <div className="home-live-sub muted">
          {m.startedAgo(formatAgo(run.startedAt))}
          {run.startedBy ? ` ${m.byPerson(run.startedBy)}` : ''}
          {extra > 0 && (
            <>
              {' · '}
              <Link to="/active" onClick={(e) => e.stopPropagation()}>
                {m.moreRunning(extra)}
              </Link>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-sm home-card-cta"
        onClick={(e) => {
          e.stopPropagation()
          open()
        }}
      >
        {m.watch} <Icon name="chevron" size={13} />
      </button>
    </div>
  )
}

/** The workshop card: live caption + wall clock over the 3D vignette. */
function StageCard({ overview }: { overview: HomeOverview | undefined }) {
  const { t, formatTime } = useI18n()
  const m = t.home
  const clock = useClock(formatTime)
  const liveCount = overview?.live.length ?? 0
  const waitingCount = overview?.attention.length ?? 0
  const busy = liveCount > 0

  let caption = m.stageSetting
  if (overview) {
    if (liveCount > 0 && waitingCount > 0) {
      caption = m.stageBusyAndWaiting(liveCount, waitingCount)
    } else if (liveCount > 0) {
      caption = m.stageBusy(liveCount)
    } else if (waitingCount > 0) {
      caption = m.stagePaused
    } else {
      caption = m.stageQuiet
    }
  }

  return (
    <div className="home-stage card">
      <div className="home-stage-head">
        <span className={`home-stage-dot${busy ? ' is-busy' : ''}`} aria-hidden />
        <span className="home-stage-caption">{caption}</span>
        <span className="home-stage-clock mono">{clock}</span>
      </div>
      <div className="home-stage-scene" aria-hidden>
        <HomeDiorama busy={busy} />
      </div>
    </div>
  )
}

/** Locale-formatted wall clock, refreshed every 30s. */
function useClock(format: (value: Date) => string): string {
  const formatRef = useRef(format)
  formatRef.current = format
  const [label, setLabel] = useState(() => format(new Date()))
  useEffect(() => {
    const tick = () => setLabel(formatRef.current(new Date()))
    tick()
    const t = setInterval(tick, 30_000)
    return () => clearInterval(t)
  }, [])
  return label
}
